import * as R from "ramda";
import { extent } from "d3-array";
import { any, arrayOf, bool, number, oneOf, shape, string } from "prop-types";

// datacenter values for datatypes
export const NUMBER = "Number";
export const PERCENT = "Percent";
export const CURRENCY = "Currency";
export const TEXT = "Text";
export const RATE = "Rate";

export const DATA_TYPES = [NUMBER, PERCENT, CURRENCY, TEXT, RATE];

// datacenter values for location type
export const COUNTRY = "Country";
export const REGION = "Region";
const COUNTRY_GROUP = "Country Group";

const LOCATION_TYPES = [COUNTRY, REGION, COUNTRY_GROUP];

export const indicatorShape = shape({
  id: number.isRequired,
  name: string.isRequired,
  indicatorGroup: string,
  category: string,
  description: string,
  yearUpdated: number,
  source: string,
  sourceAbbreviation: string,
});

export const locationFields = {
  id: number.isRequired,
  name: string.isRequired,
  type: oneOf(LOCATION_TYPES),
  slug: string,
  countryCode: string, // for country types
};

export const dataPointShape = shape({
  timeFrame: string.isRequired, // year
  characteristic: string, // eg. Male Female
  location: number, // location.id
  value: number, // could be null, via fillInNulls
  exclude: bool,
  displayValue: string, // eg 24.5%
});

// data points for a given indicator, not to be confused with an "indicator"
export const indicatorDataShape = shape({
  indicator: indicatorShape.isRequired,
  dataType: oneOf(DATA_TYPES).isRequired,
  locations: arrayOf(shape(locationFields)).isRequired,
  dataPoints: arrayOf(dataPointShape).isRequired,
  distributions: arrayOf(
    shape({
      name: string.isRequired,
      characteristics: arrayOf(string).isRequired,
    }),
  ),
});

export const lineDataShape = shape({
  id: any.isRequired,
  dataType: oneOf(DATA_TYPES).isRequired,
  name: string, // only needed if we're showing line labels
  dataPoints: arrayOf(dataPointShape).isRequired,
});

/* functions */

/**
 * fillInNulls
 * make an entry for every year and fill it with null if we don't have
 * data for that year
 * to be used with https://github.com/d3/d3-shape#line_defined
 * @param  {Array<dataPointShape>} dataPoints
 * @return {Array<dataPointShape>} a new dataPoints array with nulls filled in
 */
export const fillInNulls = (dataPoints) => {
  const dataByTimeframe = R.indexBy(R.prop("timeFrame"), dataPoints);

  return R.compose(
    R.map((timeFrameNumber) => {
      const timeFrame = timeFrameNumber.toString();
      return (
        dataByTimeframe[timeFrame] || {
          timeFrame,
          value: null,
        }
      );
    }),
    ([start, end]) => R.range(start, end + 1), // range end is exclusive
    R.map(Number),
    extent,
    R.pluck("timeFrame"),
  )(dataPoints);
};

/**
 * sortDataPoints
 * @param  {Array<dataPointShape>} dataPoints
 * @return {Array<dataPointShape>} dataPoints sorted by the timeFrame (year)
 */
export const sortDataPoints = (dataPoints) =>
  R.sortBy(R.prop("timeFrame"), dataPoints);

/**
 * filterDataPointsToYearRange
 * @param  {Array<Number>} yearRange [start, end]
 * @param  {Array<dataPointShape>} dataPoints
 */
export const filterDataPointsToYearRange = R.curry((yearRange, dataPoints) => {
  return R.filter((d) => {
    const year = parseInt(d.timeFrame);
    return year >= yearRange[0] && year <= yearRange[1];
  }, dataPoints);
});

/**
 * getDataByLocation
 * @param  {indicatorDataShape} indicatorData
 * @return {Object<lineDataShape>} indexed object of lineDataShape, eg, { 81: {lineDataShape} }
 */
export const getDataByLocation = (indicatorData) => {
  if (!indicatorData) {
    throw "`indicatorData` not provded to `getDataByLocation`";
  }

  const dataPointsByLocation = R.compose(
    R.map(sortDataPoints),
    R.groupBy(R.prop("location")),
    R.when(
      R.isEmpty,
      R.tap(() =>
        console.error(
          "Warning: indicatorData passed to `getDataByLocation` has no dataPoints!",
          indicatorData,
        ),
      ),
    ),
    R.prop("dataPoints"),
  )(indicatorData);

  return R.compose(
    R.indexBy(R.prop("id")),
    R.map((location) => {
      return {
        ...location,
        dataPoints: dataPointsByLocation[location.id] || [],
        dataType: indicatorData.dataType,
        indicator: indicatorData.indicator,
      };
    }),
  )(indicatorData.locations);
};

/**
 * getDataByDistribution
 * @param  {indicatorDataShape} data
 * @return {Object<lineDataShape>} indexed object of lineDataShape, eg, { 81: {lineDataShape} }
 */
export const getDataByDistribution = (data) => {
  if (!data) {
    throw "`data` not provded to `getDataByDistribution`";
  }

  const dataPointsByCharacteristic = R.compose(
    R.map(sortDataPoints),
    R.groupBy(R.prop("characteristic")),
    R.prop("dataPoints"),
  )(data);

  return R.compose(
    R.indexBy(R.prop("characteristic")),
    R.map((pair) => {
      const [characteristic, dataPoints] = pair;
      return {
        id: characteristic,
        characteristic,
        name: characteristic,
        dataPoints,
        dataType: data.dataType,
        indicator: data.indicator,
      };
    }),
    R.toPairs,
  )(dataPointsByCharacteristic);
};
